/**

Coded by www.techhighway.ca

*/

import { useEffect, useState } from "react";

// react-router-dom components
import { NavLink, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import List from "@mui/material/List";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "components/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "components/Sidenav/SidenavRoot";
import authPermission from "utility/auth-permissions";

// logo
import logo from "assets/images/favicon.png";

// Material Dashboard 2 React context
import {
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  useMaterialUIController,
} from "context";

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (!miniSidenav) {
      setMiniSidenav(dispatch, true); // true means collapsed
    }
  }, [location.pathname]);

  const handleMenuClick = (index) => {
    // index?.map((ind) => {
    setOpenIndex(openIndex === index ? null : index); // Toggle the clicked menu
    // })
  };

  const { hasPermission } = authPermission();

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      // setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)

  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, collapse, target }, index) => {
      let returnValue;
      if (
        type === "collapse" &&
        (hasPermission(route).view || hasPermission(href).view || hasPermission(name).view)
      ) {
        returnValue = href ? (
          <Link
            href={href}
            key={`link-${key}-${index}`}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              title={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : collapse?.length > 0 ? (
          <SidenavCollapse
            key={`sidenav-${key}-${index}`}
            name={name}
            title={name}
            icon={icon}
            active={key === collapseName}
            collapse={collapse}
            open={openIndex === index}
            onClick={() => handleMenuClick(index)}
            parentindex={[index]}
          />
        ) : (
          <NavLink key={`navlink-${key}-${index}`} to={route} target={target || ""}>
            <SidenavCollapse
              name={name}
              title={name}
              icon={icon}
              active={key === collapseName}
              collapse={collapse || []}
              target={target || ""}
              open={openIndex === index}
              onClick={() => handleMenuClick(index)}
              parentindex={[index]}
            />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key || index}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key || index}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return returnValue;
    }
  );

  const renderExampleRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, collapse }, index) => {
      let returnValue;

      if (
        type === "examples" &&
        (hasPermission(route).view || hasPermission(href).view || hasPermission(name).view)
      ) {
        returnValue = href ? (
          <Link
            href={href}
            key={`link-${key}-${index}`}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              key={`sidenav-${key}-${index}`}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={`navlink-${key}-${index}`} to={route}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              collapse={collapse || []}
            />
          </NavLink>
        );
      }
      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={1} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold", color: "#fff" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/closed-listings"
          display="inline-block"
          alignItems="center"
          sx={{ background: "transparent" }}
        >
          {!miniSidenav && brand ? (
            <MDBox component="img" src={brand} alt="Brand" width="90%" />
          ) : (
            <MDBox component="img" src={logo} alt="logo" width="40px" />
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>
        {/* <MDBox display="flex flex-col" alignItems="center">
          <MDTypography color={textColor} variant="body2" fontWeight="medium" pl="1.5rem">
            Examples
          </MDTypography>
          {renderExampleRoutes}
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        ></Divider> */}
        {renderRoutes}
      </List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
// Sidenav.defaultProps = {
//   color: "info",
//   brand: "",
// };

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
